import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { NativeRead } from '../api/envelope'
import type { ErrorVm } from './types'

/**
 * Виды цен базы 1С (RP-FB39): справочник «Виды цен» — розничная, закупочная, любые другие. Окно «Цены» базы — их список с
 * созданием, правкой и пометкой удаления, как в 1С; цены товаров — внутри вида.
 */
export interface PriceType {
  key: string
  name: string
  code: string | null
  predefined: boolean
  includesVat: boolean
  baseTypeKey: string | null
  baseTypeName: string | null
  percent: number | null
  comment: string | null
  /** Тип вида цен словом базы: «Статический», «Рассчитываемый»… */
  kind: string | null
  inactive: boolean
  deletionMark: boolean
}

export interface PriceTypeList {
  items: readonly PriceType[]
}

/** Черновик формы вида цен — намерение человека на время экрана. */
export interface PriceTypeDraft {
  /** `null` — новый вид; иначе ключ правимого. */
  key: string | null
  name: string
  includesVat: boolean
  comment: string
  inactive: boolean
}

export const NEW_PRICE_TYPE_DRAFT: PriceTypeDraft = { key: null, name: '', includesVat: true, comment: '', inactive: false }

export const MAX_PRICE_TYPE_NAME = 100

export function priceTypeDraftProblem(draft: PriceTypeDraft): string | null {
  const name = draft.name.trim()
  if (name === '') return 'Введите название вида цен'
  if (name.length > MAX_PRICE_TYPE_NAME) return `Название — не длиннее ${MAX_PRICE_TYPE_NAME} символов`
  return null
}

export type PriceTypeStateKind = 'active' | 'inactive' | 'marked'

export interface PriceTypeRowVm {
  key: string
  name: string
  code: string
  kind: string
  vat: string
  /** «От «Розничная» +10 %» у рассчитываемого вида; иначе прочерк. */
  basis: string
  comment: string
  state: string
  stateKind: PriceTypeStateKind
  predefined: boolean
  canEdit: boolean
  /** Действие пометки: пометить или снять пометку. */
  markLabel: string
  canMark: boolean
}

export interface PriceTypesVm {
  query: QueryView
  rows: readonly PriceTypeRowVm[]
  form: {
    open: boolean
    draft: PriceTypeDraft
    problem: string | null
    saving: boolean
    error: ErrorVm | null
  } | null
  /** Ключ вида, для которого ждём подтверждения пометки удаления. */
  confirmMarkKey: string | null
  marking: boolean
  markError: ErrorVm | null
  done: string | null
  canWrite: boolean
  /** Почему кнопки записи выключены (нет права у базы); `null` — право есть. */
  writeDisabledReason: string | null
}

export interface PriceTypesActions {
  reload: () => void
  create: () => void
  edit: (key: string) => void
  draftChanged: (draft: PriceTypeDraft) => void
  closeForm: () => void
  save: () => void
  askMark: (key: string) => void
  cancelMark: () => void
  confirmMark: () => void
}

/**
 * Тип вида цен словами человека. 1С отдаёт перечисление («Статический»); «Статический» — цены вводят вручную, любой другой тип
 * (или вид с базовым видом) — цена считается от другого вида. Незнакомое слово базы показывается как есть, а не гадается.
 */
export function kindWords(kind: string | null, hasBaseType: boolean): string {
  if (kind === null) return hasBaseType ? 'Считается от другого вида' : '—'
  if (/статич/i.test(kind)) return 'Вводится вручную'
  if (hasBaseType || /динамич|рассчит/i.test(kind)) return 'Считается от другого вида'
  return kind
}

const percentText = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 })

function stateOf(type: PriceType): { state: string; stateKind: PriceTypeStateKind } {
  if (type.deletionMark) return { state: 'Помечен на удаление', stateKind: 'marked' }
  if (type.inactive) return { state: 'Недействителен', stateKind: 'inactive' }
  return { state: 'Действует', stateKind: 'active' }
}

export function priceTypeRow(type: PriceType): PriceTypeRowVm {
  const basis =
    type.baseTypeKey === null
      ? '—'
      : `От «${type.baseTypeName ?? 'вид без имени'}»${type.percent === null ? '' : ` ${type.percent >= 0 ? '+' : '−'}${percentText.format(Math.abs(type.percent))} %`}`
  return {
    key: type.key,
    name: type.name === '' ? 'Без названия' : type.name,
    code: type.code ?? '—',
    kind: kindWords(type.kind, type.baseTypeKey !== null),
    vat: type.includesVat ? 'Включает НДС' : 'Без НДС',
    basis,
    comment: type.comment ?? '',
    ...stateOf(type),
    predefined: type.predefined,
    canEdit: !type.deletionMark,
    markLabel: type.deletionMark ? 'Снять пометку удаления' : 'Пометить на удаление',
    canMark: !(type.predefined && !type.deletionMark),
  }
}

const EMPTY_ROWS: readonly PriceTypeRowVm[] = []

export const makeSelectPriceTypeRows = () =>
  createSelector([(list: QueryResultLike<NativeRead<PriceTypeList>>) => list], (list) => {
    const query = queryView(list, (read) => read.data.items.length === 0)
    const rows = query.status === 'ready' && list.data !== undefined ? list.data.data.items.map(priceTypeRow) : EMPTY_ROWS
    return { query, rows }
  })
